// Daos Ads /studio — Canvas template renderers v2.
//
// Brand-strict, layout-safe poster renderer.
//   - Safe margin: 8% on every side
//   - Bottom footer band: fixed reserved height (handle left + URL right)
//   - Header band: brand logo + "Daos Ads" wordmark
//   - Content zone is what's left between header and footer
//   - Text auto-shrinks to fit (min 28px headline, min 14px subtitle)
//   - Per-word styling: highlight selected words with the brand gradient
//     or one of the signal colors (green / amber / red)
//   - "Cita personal" is the only template where the author signature is shown
//   - FB banner (1200×628) constrains text to the LEFT half with breathing space
//     on the right
//
// Public API:
//   await renderTemplate(ctx, { width, height, templateId, content, background,
//                               logo, gradientWords, signalWord, signalColor })
//
// content = { headline, subtitle, cta, author }
// background = HTMLImageElement | null
// logo = HTMLImageElement | null   (already loaded Daos Ads PNG)
// gradientWords = string[]         (case-insensitive)
// signalWord = string | null       (case-insensitive)
// signalColor = "green" | "amber" | "red" | null

// ============================================================
// BRAND SYSTEM
// ============================================================
const BRAND = {
  bg0: "#050505",      // Negro Daos
  bg1: "#0A0A0A",      // Carbón
  bg2: "#111111",      // Grafito
  border10: "#1A1A1A", // Gris 10
  white: "#FFFFFF",
  gray300: "#D4D4D8",
  gray500: "#71717A",
  gray700: "#3F3F46",
};

const SIGNAL_COLORS = {
  green: "#25D366",
  amber: "#FFB020",
  red:   "#F43F5E",
};

const GRADIENT_STOPS = [
  { stop: 0,   color: "#FF3B1F" }, // Rojo Daos
  { stop: 0.5, color: "#FF8A00" }, // Naranja Daos
  { stop: 1,   color: "#FFD23F" }, // Dorado Daos
];

const FONT_DISPLAY = '"Sora", "Inter", system-ui, sans-serif';

// Safe-area rules
const SAFE_RATIO = 0.08;
const HEADLINE_MIN_PX = 28;
const SUBTITLE_MIN_PX = 14;

// ============================================================
// LOW-LEVEL HELPERS
// ============================================================
function makeGradient(ctx, x0, y0, x1, y1) {
  const g = ctx.createLinearGradient(x0, y0, x1, y1);
  GRADIENT_STOPS.forEach((s) => g.addColorStop(s.stop, s.color));
  return g;
}

function normWord(w) {
  // Normalize for case-insensitive word matching (strip punctuation, accents kept).
  return String(w || "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
}

// Convert "Gana más, pierde, cero" → [["gana","mas"], ["pierde"], ["cero"]]
// Each entry is a sequence of normalized words that should be matched as a phrase.
function parsePhrases(input) {
  if (Array.isArray(input)) {
    return input
      .map((p) => String(p || "").split(/\s+/).map(normWord).filter(Boolean))
      .filter((arr) => arr.length > 0);
  }
  return String(input || "")
    .split(",")
    .map((p) => p.split(/\s+/).map(normWord).filter(Boolean))
    .filter((arr) => arr.length > 0);
}

// Apply phrase matches over an array of word tokens. Tokens whose `style` is
// already higher-priority will not be downgraded.
function applyPhraseStyle(tokens, phrases, style) {
  const PRIORITY = { normal: 0, gradient: 1, signal: 2 };
  for (const phrase of phrases) {
    const L = phrase.length;
    if (!L) continue;
    for (let i = 0; i <= tokens.length - L; i++) {
      let match = true;
      for (let j = 0; j < L; j++) {
        if (normWord(tokens[i + j].text) !== phrase[j]) { match = false; break; }
      }
      if (match) {
        for (let j = 0; j < L; j++) {
          if (PRIORITY[style] >= PRIORITY[tokens[i + j].style]) {
            tokens[i + j].style = style;
          }
        }
      }
    }
  }
}

function tokenize(text, gradientWords = null, signalWords = null) {
  const tokens = String(text || "")
    .split(/\s+/).filter(Boolean)
    .map((t) => ({ text: t, style: "normal" }));

  applyPhraseStyle(tokens, parsePhrases(gradientWords), "gradient");
  applyPhraseStyle(tokens, parsePhrases(signalWords), "signal"); // signal wins
  return tokens;
}

// Wrap tokens into lines that fit `maxWidth` at the current font.
function wrapTokens(ctx, tokens, maxWidth) {
  if (!tokens.length) return [];
  const spaceW = ctx.measureText(" ").width;
  const lines = [];
  let current = [];
  let currentWidth = 0;
  for (const tok of tokens) {
    const w = ctx.measureText(tok.text).width;
    const addedWidth = current.length === 0 ? w : currentWidth + spaceW + w;
    if (current.length > 0 && addedWidth > maxWidth) {
      lines.push({ tokens: current, width: currentWidth });
      current = [tok];
      currentWidth = w;
    } else {
      current.push(tok);
      currentWidth = addedWidth;
    }
  }
  if (current.length) lines.push({ tokens: current, width: currentWidth });
  return lines;
}

// Auto-shrink font so the wrapped block fits maxHeight (numLines * lineHeight).
// Returns { size, lineHeight, lines } where lines is the wrapTokens output at that size.
function fitStyledBlock(ctx, tokens, opts) {
  const {
    maxWidth, maxHeight, startSize, minSize, weight = 800,
    family = FONT_DISPLAY, lineHeightRatio = 1.08,
  } = opts;

  let size = startSize;
  let lines = [];
  let lineHeight = 0;

  for (; size >= minSize; size -= 2) {
    ctx.font = `${weight} ${size}px ${family}`;
    lines = wrapTokens(ctx, tokens, maxWidth);
    lineHeight = Math.round(size * lineHeightRatio);
    if (lines.length * lineHeight <= maxHeight) return { size, lines, lineHeight };
  }
  // Fell through: clamp to min size; truncate to lines that fit.
  const maxLines = Math.max(1, Math.floor(maxHeight / lineHeight));
  if (lines.length > maxLines) {
    lines = lines.slice(0, maxLines);
    // Ellipsize last line if anything was dropped.
    const last = lines[lines.length - 1];
    last.tokens = [...last.tokens, { text: "…", style: "normal" }];
    ctx.font = `${weight} ${size}px ${family}`;
    last.width = last.tokens.reduce(
      (acc, t, i) => acc + (i === 0 ? 0 : ctx.measureText(" ").width) + ctx.measureText(t.text).width,
      0,
    );
  }
  return { size, lines, lineHeight };
}

function drawStyledLines(ctx, fitted, { x, yTop, align = "left", signalColor = "amber", color = BRAND.white }) {
  const { lines, lineHeight, size } = fitted;
  ctx.textBaseline = "alphabetic";
  const baselineOffset = Math.round(size * 0.82);
  const signalHex = SIGNAL_COLORS[signalColor] || SIGNAL_COLORS.amber;

  lines.forEach((line, i) => {
    let startX = x;
    if (align === "center") startX = x - line.width / 2;
    if (align === "right") startX = x - line.width;
    let cursor = startX;
    const y = yTop + i * lineHeight + baselineOffset;
    const spaceW = ctx.measureText(" ").width;

    line.tokens.forEach((tok, ti) => {
      if (ti > 0) cursor += spaceW;
      const w = ctx.measureText(tok.text).width;

      if (tok.style === "gradient") {
        const grad = makeGradient(ctx, cursor, y - size, cursor + w, y);
        ctx.fillStyle = grad;
      } else if (tok.style === "signal") {
        ctx.fillStyle = signalHex;
      } else {
        ctx.fillStyle = color;
      }
      ctx.textAlign = "left";
      ctx.fillText(tok.text, cursor, y);
      cursor += w;
    });
  });
}

function drawBackground(ctx, { width, height, background, accent = true }) {
  // Solid base
  ctx.fillStyle = BRAND.bg0;
  ctx.fillRect(0, 0, width, height);

  // Optional cover background
  if (background && background.complete && background.naturalWidth > 0) {
    const iw = background.naturalWidth;
    const ih = background.naturalHeight;
    const scale = Math.max(width / iw, height / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    ctx.drawImage(background, (width - dw) / 2, (height - dh) / 2, dw, dh);
    // Mandatory Daos overlay rgba(5,5,5,0.72) for legibility / brand consistency
    ctx.fillStyle = "rgba(5,5,5,0.72)";
    ctx.fillRect(0, 0, width, height);
  }

  if (accent) {
    const r1 = ctx.createRadialGradient(width * 0.85, height * 0.05, 0, width * 0.85, height * 0.05, Math.max(width, height) * 0.7);
    r1.addColorStop(0, "rgba(255,138,0,0.18)");
    r1.addColorStop(0.55, "rgba(255,138,0,0)");
    ctx.fillStyle = r1;
    ctx.fillRect(0, 0, width, height);

    const r2 = ctx.createRadialGradient(width * 0.1, height * 0.0, 0, width * 0.1, height * 0.0, Math.max(width, height) * 0.55);
    r2.addColorStop(0, "rgba(255,59,31,0.14)");
    r2.addColorStop(0.6, "rgba(255,59,31,0)");
    ctx.fillStyle = r2;
    ctx.fillRect(0, 0, width, height);
  }
}

function drawGradientStripeTop(ctx, { width, scale }) {
  ctx.fillStyle = makeGradient(ctx, 0, 0, width, 0);
  ctx.fillRect(0, 0, width, Math.max(8, Math.round(14 * scale)));
}

// Daos brand logo: PNG isotipo + "Daos Ads" wordmark (Sora 800 white).
// Falls back to a gradient "D" disc if the image hasn't loaded.
function drawBrandLogo(ctx, { x, y, image, scale = 1, height = 72, banner = false }) {
  const h = banner ? Math.round(height * 0.85) : height;
  let logoW = h;
  if (image && image.complete && image.naturalWidth > 0) {
    const aspect = image.naturalWidth / image.naturalHeight;
    logoW = Math.round(h * aspect);
    ctx.drawImage(image, x, y, logoW, h);
  } else {
    // Fallback: gradient "D" disc
    const grad = makeGradient(ctx, x, y, x + h, y + h);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x + h / 2, y + h / 2, h / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = BRAND.bg0;
    ctx.font = `800 ${Math.round(h * 0.55)}px ${FONT_DISPLAY}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("D", x + h / 2, y + h / 2 + 2);
    logoW = h;
  }

  // Wordmark
  const wordSize = Math.round(h * (banner ? 0.42 : 0.48));
  ctx.font = `800 ${wordSize}px ${FONT_DISPLAY}`;
  ctx.fillStyle = BRAND.white;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("Daos Ads", x + logoW + Math.round(18 * scale), y + Math.round(h * 0.68));
}

function drawFooter(ctx, { width, height, scale = 1, safeX, footerY }) {
  const fontSize = Math.round(22 * scale);
  ctx.font = `600 ${fontSize}px ${FONT_DISPLAY}`;
  ctx.fillStyle = BRAND.gray500;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("@DAOS.ADS", safeX, footerY);

  ctx.fillStyle = "#FF8A00";
  ctx.font = `700 ${fontSize}px ${FONT_DISPLAY}`;
  ctx.textAlign = "right";
  ctx.fillText("daosads.com →", width - safeX, footerY);
}

// CTA color palette (referenced by the "Color del CTA" selector in /studio).
const CTA_COLOR_FILLS = {
  gradient: null, // computed per pill with createLinearGradient
  orange: "#FF8A00",
  green:  "#25D366",
  amber:  "#FFB020",
  red:    "#F43F5E",
  white:  "#FFFFFF",
};

function drawCtaPill(ctx, text, { centerX, y, centerY, scale = 1, maxWidth, color = "gradient" }) {
  const txt = String(text || "").trim();
  if (!txt) return null;
  // Auto-shrink CTA pill font to respect maxWidth.
  let fontSize = Math.round(28 * scale);
  const minFont = Math.max(14, Math.round(16 * scale));
  let padX = Math.round(40 * scale);
  const label = txt.toUpperCase() + (txt.endsWith("→") ? "" : " →");

  for (; fontSize >= minFont; fontSize -= 2) {
    ctx.font = `700 ${fontSize}px ${FONT_DISPLAY}`;
    const tw = ctx.measureText(label).width;
    if (tw + padX * 2 <= maxWidth) break;
  }
  ctx.font = `700 ${fontSize}px ${FONT_DISPLAY}`;
  padX = Math.round(fontSize * 1.4);
  const padY = Math.round(fontSize * 0.7);
  const textWidth = ctx.measureText(label).width;
  const w = Math.min(textWidth + padX * 2, maxWidth);
  const h = fontSize + padY * 2;
  // If centerY was provided, override `y` to vertically-center the pill.
  const yTop = typeof centerY === "number" ? Math.round(centerY - h / 2) : y;
  const x = centerX - w / 2;
  const radius = h / 2;

  // Fill style: gradient (default) OR a solid signal/brand color
  if (color === "gradient" || !CTA_COLOR_FILLS[color]) {
    ctx.fillStyle = makeGradient(ctx, x, yTop, x + w, yTop);
  } else {
    ctx.fillStyle = CTA_COLOR_FILLS[color];
  }
  ctx.beginPath();
  ctx.moveTo(x + radius, yTop);
  ctx.lineTo(x + w - radius, yTop);
  ctx.arc(x + w - radius, yTop + radius, radius, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(x + radius, yTop + h);
  ctx.arc(x + radius, yTop + radius, radius, Math.PI / 2, -Math.PI / 2);
  ctx.closePath();
  ctx.fill();

  // Text color: dark on bright fills, dark also on white pill (#050505 for contrast).
  ctx.fillStyle = color === "white" ? BRAND.bg0 : BRAND.bg1;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, x + w / 2, yTop + h / 2 + 2);
  return { width: w, height: h, y: yTop };
}

function drawEyebrow(ctx, text, { x, y, scale = 1, align = "left", color = "#FF8A00" }) {
  const size = Math.round(22 * scale);
  ctx.font = `700 ${size}px ${FONT_DISPLAY}`;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = "alphabetic";
  const txt = String(text || "").toUpperCase();
  const spacing = size * 0.18;
  const totalWidth = [...txt].reduce((acc, ch) => acc + ctx.measureText(ch).width + spacing, -spacing);
  let cx = x;
  if (align === "center") cx = x - totalWidth / 2;
  if (align === "right") cx = x - totalWidth;
  ctx.textAlign = "left";
  for (const ch of txt) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + spacing;
  }
}

// ============================================================
// SHARED LAYOUT
// ============================================================
//
// Returns the safe zones for the requested canvas. All templates start from
// this skeleton and place their elements inside it.
function computeZones(width, height) {
  const safe = Math.round(Math.min(width, height) * SAFE_RATIO);
  const scale = width / 1080;
  const isBanner = width > height && width / height >= 1.6; // 1200×628 territory

  const headerH = Math.round((isBanner ? 70 : 90) * scale);
  const footerH = Math.round((isBanner ? 70 : 90) * scale);

  const contentTop = safe + headerH + Math.round((isBanner ? 14 : 28) * scale);
  const contentBottom = height - safe - footerH;
  // For the FB banner: text uses 60% (left column); CTA pill lives in the right
  // 40% vertically centered against the whole canvas.
  const contentLeft = safe;
  const contentRight = isBanner ? Math.round(width * 0.60) : (width - safe);

  // Right column geometry (only meaningful for the banner).
  const rightColLeft = isBanner ? Math.round(width * 0.62) : null;
  const rightColRight = isBanner ? (width - safe) : null;

  return {
    safe, scale, isBanner,
    headerY: safe,
    headerH,
    footerY: height - safe,
    footerH,
    contentLeft,
    contentRight,
    contentTop,
    contentBottom,
    contentWidth: contentRight - contentLeft,
    contentHeight: contentBottom - contentTop,
    // Right column (banner only)
    rightColLeft,
    rightColRight,
    rightColWidth: isBanner ? (rightColRight - rightColLeft) : null,
  };
}

// ============================================================
// TEMPLATE PARAMS PIPELINE
// ============================================================
//
// Each renderer receives:
//   ctx, { width, height, content, logo, scale, zones,
//          gradientWords, signalWord, signalColor }
//
// content includes already-stylized tokens too for headline/subtitle so we
// avoid recomputing in each template.

function makeContext(width, height, content, logo, opts) {
  const zones = computeZones(width, height);
  const headlineTokens = tokenize(content.headline, opts.gradientWords, opts.signalWords);
  const subtitleTokens = tokenize(content.subtitle, opts.gradientWords, opts.signalWords);
  return {
    width, height, content, logo, zones,
    headlineTokens, subtitleTokens,
    gradientWords: opts.gradientWords,
    signalWords: opts.signalWords,
    signalColor: opts.signalColor || "amber",
    ctaColor: opts.ctaColor || "gradient",
    scale: zones.scale,
  };
}

// ============================================================
// TEMPLATES
// ============================================================

// Auto-fits headline + subtitle + CTA into the content zone, respecting margins.
// Used by every "standard" template (i.e. all except cita-personal).
function drawStandardBody(ctx, p, { eyebrowText = null, headlineWeight = 800, headlineStart = 110, subtitleStart = 30 }) {
  const { zones, content, headlineTokens, subtitleTokens, signalColor, scale, ctaColor } = p;
  const { contentLeft, contentTop, contentBottom, contentWidth, isBanner } = zones;

  // For banner: CTA is in the RIGHT column → no vertical reserve needed in the
  // content zone. For other formats: reserve room above the footer for the pill.
  const ctaReserve = isBanner ? Math.round(20 * scale) : Math.round(118 * scale);
  const subtitleReserve = subtitleTokens.length ? Math.round(180 * scale) : Math.round(50 * scale);
  const eyebrowReserve = eyebrowText ? Math.round(58 * scale) : 0;

  let cursorY = contentTop;
  if (eyebrowText) {
    drawEyebrow(ctx, eyebrowText, { x: contentLeft, y: cursorY + Math.round(28 * scale), scale });
    cursorY += eyebrowReserve;
  }

  // Headline auto-fit
  const headlineMaxHeight = (contentBottom - cursorY) - subtitleReserve - ctaReserve;
  const fittedHeadline = fitStyledBlock(ctx, headlineTokens, {
    maxWidth: contentWidth,
    maxHeight: Math.max(headlineMaxHeight, Math.round(120 * scale)),
    startSize: Math.round(headlineStart * scale),
    minSize: HEADLINE_MIN_PX,
    weight: headlineWeight,
    lineHeightRatio: 1.04,
  });
  drawStyledLines(ctx, fittedHeadline, { x: contentLeft, yTop: cursorY, signalColor });
  cursorY += fittedHeadline.lines.length * fittedHeadline.lineHeight + Math.round(24 * scale);

  // Subtitle auto-fit
  if (subtitleTokens.length) {
    const remaining = (contentBottom - cursorY) - ctaReserve;
    const fittedSub = fitStyledBlock(ctx, subtitleTokens, {
      maxWidth: contentWidth,
      maxHeight: Math.max(remaining, Math.round(60 * scale)),
      startSize: Math.round(subtitleStart * scale),
      minSize: SUBTITLE_MIN_PX,
      weight: 400,
      lineHeightRatio: 1.32,
    });
    drawStyledLines(ctx, fittedSub, { x: contentLeft, yTop: cursorY, signalColor, color: BRAND.gray300 });
    cursorY += fittedSub.lines.length * fittedSub.lineHeight;
  }

  // CTA pill
  if (content.cta) {
    if (isBanner) {
      // Banner: CTA lives in the RIGHT column, vertically centered against the canvas.
      drawCtaPill(ctx, content.cta, {
        centerX: zones.rightColLeft + zones.rightColWidth / 2,
        centerY: p.height / 2,
        scale,
        maxWidth: zones.rightColWidth,
        color: ctaColor,
      });
    } else {
      // Standard formats: CTA centered horizontally above the footer band.
      drawCtaPill(ctx, content.cta, {
        centerX: p.width / 2,
        y: contentBottom - Math.round(72 * scale),
        scale,
        maxWidth: contentWidth,
        color: ctaColor,
      });
    }
  }
}

// Big quote mark, positioned to NEVER overlap the brand logo header. Used by
// both tplFrase and tplCitaPersonal.
function drawBigQuote(ctx, p, fontSize) {
  const { scale, zones } = p;
  ctx.font = `800 ${fontSize}px ${FONT_DISPLAY}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  // Top of the glyph must sit 24px below the logo band.
  const topY = zones.headerY + zones.headerH + Math.round(24 * scale);
  const baselineY = topY + Math.round(fontSize * 0.78);
  const qw = ctx.measureText("\u201C").width;
  const grad = makeGradient(ctx, zones.contentLeft, topY, zones.contentLeft + qw, baselineY);
  ctx.fillStyle = grad;
  ctx.fillText("\u201C", zones.contentLeft, baselineY);
  return { topY, baselineY };
}

function tplFrase(ctx, p) {
  // Big opening quote mark, then headline + subtitle + CTA via standard body.
  const { scale, zones } = p;
  // Smaller quote on the banner so it doesn't dominate the short 628px height.
  const quoteFont = Math.round((zones.isBanner ? 110 : 160) * scale);
  const { baselineY } = drawBigQuote(ctx, p, quoteFont);

  const shifted = {
    ...p,
    zones: { ...zones, contentTop: baselineY + Math.round(24 * scale) },
  };
  drawStandardBody(ctx, shifted, { eyebrowText: null, headlineStart: 96 });
}

function tplTip(ctx, p) {
  drawStandardBody(ctx, p, { eyebrowText: "Tip · Insight", headlineStart: 108 });
}

function tplServicio(ctx, p) {
  drawStandardBody(ctx, p, { eyebrowText: "Servicio", headlineStart: 124 });
}

function tplCasoExito(ctx, p) {
  drawStandardBody(ctx, p, { eyebrowText: "Caso de éxito", headlineStart: 120 });
}

function tplAntesDespues(ctx, p) {
  // Single accent divider above the headline, label "Después · con Daos Ads"
  const { scale, zones } = p;
  drawEyebrow(ctx, "Después · con Daos Ads", {
    x: zones.contentLeft, y: zones.contentTop + Math.round(28 * scale), scale,
  });
  const dividerY = zones.contentTop + Math.round(56 * scale);
  const gradLine = ctx.createLinearGradient(zones.contentLeft, dividerY, zones.contentLeft + zones.contentWidth, dividerY);
  gradLine.addColorStop(0, "rgba(255,59,31,0)");
  gradLine.addColorStop(0.5, "rgba(255,138,0,1)");
  gradLine.addColorStop(1, "rgba(255,210,63,0)");
  ctx.fillStyle = gradLine;
  ctx.fillRect(zones.contentLeft, dividerY, zones.contentWidth, Math.max(2, Math.round(3 * scale)));

  const shifted = { ...p, zones: { ...zones, contentTop: zones.contentTop + Math.round(86 * scale) } };
  drawStandardBody(ctx, shifted, { eyebrowText: null, headlineStart: 96 });
}

function tplAnuncioCta(ctx, p) {
  drawStandardBody(ctx, p, { eyebrowText: "Oferta", headlineStart: 130 });
}

// Cita personal — the ONLY template that shows the author signature.
function tplCitaPersonal(ctx, p) {
  const { scale, zones, content, headlineTokens, signalColor } = p;

  // Quote mark — positioned below the brand logo header (never overlap).
  const quoteFont = Math.round((zones.isBanner ? 110 : 180) * scale);
  const { baselineY } = drawBigQuote(ctx, p, quoteFont);

  // Reserve space for the bottom author chip (above footer).
  const authorChipH = Math.round(110 * scale);
  const headlineTop = baselineY + Math.round(28 * scale);
  const headlineMaxH = zones.contentBottom - headlineTop - authorChipH - Math.round(20 * scale);

  const fittedHeadline = fitStyledBlock(ctx, headlineTokens, {
    maxWidth: zones.contentWidth,
    maxHeight: headlineMaxH,
    startSize: Math.round(80 * scale),
    minSize: HEADLINE_MIN_PX,
    weight: 600,
    lineHeightRatio: 1.18,
  });
  drawStyledLines(ctx, fittedHeadline, { x: zones.contentLeft, yTop: headlineTop, signalColor });

  // Author chip (bottom-left, above footer)
  const chipY = zones.contentBottom - authorChipH;
  const discSize = Math.round(80 * scale);
  const gd = makeGradient(ctx, zones.contentLeft, chipY, zones.contentLeft + discSize, chipY + discSize);
  ctx.fillStyle = gd;
  ctx.beginPath();
  ctx.arc(zones.contentLeft + discSize / 2, chipY + discSize / 2, discSize / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = BRAND.bg1;
  ctx.font = `800 ${Math.round(36 * scale)}px ${FONT_DISPLAY}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("D", zones.contentLeft + discSize / 2, chipY + discSize / 2 + 2);

  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.font = `700 ${Math.round(30 * scale)}px ${FONT_DISPLAY}`;
  ctx.fillStyle = BRAND.white;
  ctx.fillText(content.author || "David Osorio", zones.contentLeft + discSize + Math.round(20 * scale), chipY + Math.round(34 * scale));
  ctx.font = `400 ${Math.round(22 * scale)}px ${FONT_DISPLAY}`;
  ctx.fillStyle = BRAND.gray500;
  ctx.fillText("Fundador · Daos Ads", zones.contentLeft + discSize + Math.round(20 * scale), chipY + Math.round(64 * scale));
}

const TEMPLATES = {
  "frase":          tplFrase,
  "tip":            tplTip,
  "servicio":       tplServicio,
  "caso-exito":     tplCasoExito,
  "antes-despues":  tplAntesDespues,
  "anuncio-cta":    tplAnuncioCta,
  "cita-personal":  tplCitaPersonal,
};

// Some templates carry an accent stripe at the top to emphasise the band.
const STRIPE_TEMPLATES = new Set(["tip", "servicio", "anuncio-cta"]);

export const TEMPLATE_OPTIONS = [
  { id: "frase",         label: "Frase / Manifiesto" },
  { id: "tip",           label: "Tip / Insight" },
  { id: "servicio",      label: "Servicio destacado" },
  { id: "caso-exito",    label: "Caso de éxito (KPI)" },
  { id: "antes-despues", label: "Antes vs Después" },
  { id: "anuncio-cta",   label: "Anuncio con CTA" },
  { id: "cita-personal", label: "Cita personal (David)" },
];

export const FORMAT_OPTIONS = [
  { id: "ig-square",   label: "IG Post · 1080×1080",     width: 1080, height: 1080, aspect: "square" },
  { id: "ig-vertical", label: "IG Vertical · 1080×1350", width: 1080, height: 1350, aspect: "portrait" },
  { id: "story",       label: "Story / Reel · 1080×1920", width: 1080, height: 1920, aspect: "story" },
  { id: "fb-banner",   label: "FB Banner · 1200×628",    width: 1200, height: 628,  aspect: "landscape" },
];

export const CTA_PRESETS = [
  { id: "url",        label: "daosads.com",            value: "daosads.com" },
  { id: "quote",      label: "Cotiza tu proyecto",     value: "Cotiza tu proyecto" },
  { id: "free",       label: "Asesoría gratis",        value: "Asesoría gratis" },
  { id: "contact",    label: "Contáctame",             value: "Contáctame" },
  { id: "call",       label: "Agenda una llamada",     value: "Agenda una llamada" },
  { id: "write",      label: "Escríbeme",              value: "Escríbeme" },
  { id: "url-form",   label: "daosads.com/contacto",   value: "daosads.com/contacto" },
];

export const SIGNAL_COLOR_OPTIONS = [
  { id: "none",  label: "Sin señal",   value: null,    swatch: null },
  { id: "green", label: "Éxito",       value: "green", swatch: SIGNAL_COLORS.green },
  { id: "amber", label: "Advertencia", value: "amber", swatch: SIGNAL_COLORS.amber },
  { id: "red",   label: "Dolor",       value: "red",   swatch: SIGNAL_COLORS.red },
];

// CTA pill color selector — gradient is the default brand option.
export const CTA_COLOR_OPTIONS = [
  { id: "gradient", label: "Degradado de marca", swatch: "linear-gradient(135deg,#FF3B1F 0%,#FF8A00 50%,#FFD23F 100%)" },
  { id: "orange",   label: "Naranja sólido",     swatch: CTA_COLOR_FILLS.orange },
  { id: "green",    label: "Verde",              swatch: CTA_COLOR_FILLS.green },
  { id: "amber",    label: "Ámbar",              swatch: CTA_COLOR_FILLS.amber },
  { id: "red",      label: "Rojo",               swatch: CTA_COLOR_FILLS.red },
  { id: "white",    label: "Blanco",             swatch: CTA_COLOR_FILLS.white },
];

// Public renderer
export async function renderTemplate(ctx, opts) {
  const {
    width, height, templateId, content, background, logo,
    gradientWords = null, signalWords = null, signalColor = null,
    ctaColor = "gradient",
  } = opts;

  const fn = TEMPLATES[templateId] || TEMPLATES["frase"];
  drawBackground(ctx, { width, height, background, accent: true });

  const p = makeContext(width, height, content, logo, {
    gradientWords, signalWords, signalColor, ctaColor,
  });

  // Top accent stripe for selected templates.
  if (STRIPE_TEMPLATES.has(templateId)) {
    drawGradientStripeTop(ctx, { width, scale: p.scale });
  }

  // Brand header (logo + wordmark) — top-left, inside safe area.
  const logoH = p.zones.isBanner ? Math.round(62 * p.scale) : Math.round(72 * p.scale);
  drawBrandLogo(ctx, {
    x: p.zones.contentLeft,
    y: p.zones.headerY,
    image: logo,
    scale: p.scale,
    height: logoH,
    banner: p.zones.isBanner,
  });

  // Template-specific body
  fn(ctx, p);

  // Footer band — fixed bottom (handle left, URL right). Always rendered last.
  drawFooter(ctx, {
    width, height, scale: p.scale,
    safeX: p.zones.contentLeft,
    footerY: p.zones.footerY,
  });
}

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
